import { useEffect, useMemo, useRef, useState } from "react";
import { DEMOS } from "@/lib/exerciseDemos";
import { Play, Pause, Gauge } from "lucide-react";

const L = { shin: 46, thigh: 50, torso: 60, uarm: 31, farm: 29, neck: 9, head: 9 };
const rad = (d) => (d * Math.PI) / 180;
const step = (p, len, deg) => ({ x: p.x + len * Math.sin(rad(deg)), y: p.y - len * Math.cos(rad(deg)) });
const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

function pose(a, root) {
  if (root === "hands") {
    const hand = { x: 150, y: 46 };
    const elbow = step(hand, L.farm, a.farm);
    const shoulder = step(elbow, L.uarm, a.uarm);
    const hip = step(shoulder, L.torso, a.torso);
    const knee = step(hip, L.thigh, a.thigh);
    const ankle = step(knee, L.shin, a.shin);
    const head = step(shoulder, L.neck + L.head, a.torso + 180);
    return { ankle, knee, hip, shoulder, elbow, hand, head, toe: step(ankle, 12, a.shin + 90) };
  }
  const ankle = { x: a.x ?? 150, y: 228 };
  const knee = step(ankle, L.shin, a.shin);
  const hip = step(knee, L.thigh, a.thigh);
  const shoulder = step(hip, L.torso, a.torso);
  const head = step(shoulder, L.neck + L.head, a.torso);
  const uAbs = a.torso + a.uarm;
  const elbow = step(shoulder, L.uarm, uAbs);
  const hand = step(elbow, L.farm, uAbs + a.farm);
  return { ankle, knee, hip, shoulder, elbow, hand, head, toe: { x: ankle.x + 15, y: 232 } };
}

function sample(frames, t) {
  let i = 0;
  while (i < frames.length - 2 && t > frames[i + 1].t) i++;
  const A = frames[i], B = frames[i + 1];
  const u = B.t === A.t ? 1 : ease(Math.min(1, Math.max(0, (t - A.t) / (B.t - A.t))));
  const a = {};
  for (const key of new Set([...Object.keys(A.a), ...Object.keys(B.a)])) a[key] = (A.a[key] ?? 0) + ((B.a[key] ?? 0) - (A.a[key] ?? 0)) * u;
  return { a, cue: (u > 0.5 ? B : A).cue };
}

export function ExerciseDemo({ name, size = 300, accent = "#CCFF00", showCues = true, fixedT }) {
  const demo = DEMOS[name];
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(1);
  const raf = useRef(0), last = useRef(0);
  const period = 3.2;
  const trail = useMemo(() => {
    if (!demo || demo.load === "none") return "";
    const pts = [];
    for (let i = 0; i <= 60; i++) {
      const p = pose(sample(demo.frames, i / 60).a, demo.root);
      const b = demo.barAt === "back" ? { x: p.shoulder.x - 4, y: p.shoulder.y + 2 } : p.hand;
      pts.push(`${b.x.toFixed(1)},${b.y.toFixed(1)}`);
    }
    return pts.join(" ");
  }, [demo]);

  useEffect(() => {
    if (!playing || fixedT != null) return undefined;
    const tick = (ts) => {
      if (last.current) setT((x) => (x + ((ts - last.current) / 1000) * speed / period) % 1);
      last.current = ts;
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf.current); last.current = 0; };
  }, [playing, speed, fixedT]);

  if (!demo) return null;
  const { a, cue } = sample(demo.frames, fixedT ?? t);
  const p = pose(a, demo.root);
  const bar = demo.barAt === "back" ? { x: p.shoulder.x - 4, y: p.shoulder.y + 2 } : p.hand;
  const line = (A, B, w = 7, c = "#E8ECF3") => <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke={c} strokeWidth={w} strokeLinecap="round" />;

  return (
    <div className="rounded-xl border border-[#1E222A] bg-[radial-gradient(circle_at_50%_30%,#141821,#07080B)] overflow-hidden" data-testid="exercise-demo">
      <svg viewBox="0 0 300 250" width="100%" style={{ maxWidth: size, display: "block", margin: "0 auto" }} role="img" aria-label={`${name} technique animation`}>
        <defs>
          <filter id="glow"><feGaussianBlur stdDeviation="2.5" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M20 0H0V20" fill="none" stroke="#ffffff08" /></pattern>
        </defs>
        <rect width="300" height="250" fill="url(#grid)" />
        <line x1="0" y1="233" x2="300" y2="233" stroke="#343A4A" strokeWidth="2" />
        {demo.load === "pullbar" && <line x1="95" y1="46" x2="205" y2="46" stroke="#8A91A6" strokeWidth="5" strokeLinecap="round" />}
        {trail && <polyline points={trail} fill="none" stroke={accent} strokeOpacity="0.35" strokeWidth="1.5" strokeDasharray="3 4" />}
        <g filter="url(#glow)">
          {line(p.ankle, p.toe, 6, "#B7BECB")}
          {line(p.ankle, p.knee)}{line(p.knee, p.hip, 8)}{line(p.hip, p.shoulder, 9)}
          {line(p.shoulder, p.elbow, 6, "#D7DCE5")}{line(p.elbow, p.hand, 5, "#D7DCE5")}
          <circle cx={p.head.x} cy={p.head.y} r={L.head} fill="#E8ECF3" />
          {[p.knee, p.hip, p.shoulder, p.elbow].map((j, i) => <circle key={i} cx={j.x} cy={j.y} r="3.2" fill={accent} />)}
        </g>
        {demo.load === "bar" && (
          <g>
            <line x1={bar.x - 6} y1={bar.y} x2={bar.x + 6} y2={bar.y} stroke="#8A91A6" strokeWidth="4" />
            <circle cx={bar.x} cy={bar.y} r="21" fill="none" stroke={accent} strokeWidth="5" opacity="0.9" />
            <circle cx={bar.x} cy={bar.y} r="3" fill="#E8ECF3" />
          </g>
        )}
        {demo.load === "kb" && <g><line x1={p.hand.x} y1={p.hand.y} x2={p.hand.x + (p.hand.x - p.elbow.x) * 0.3} y2={p.hand.y + (p.hand.y - p.elbow.y) * 0.3} stroke="#8A91A6" strokeWidth="3" /><circle cx={p.hand.x + (p.hand.x - p.elbow.x) * 0.55} cy={p.hand.y + (p.hand.y - p.elbow.y) * 0.55} r="11" fill={accent} /></g>}
        {demo.load === "ball" && <circle cx={p.hand.x + 6} cy={p.hand.y - 4} r="12" fill={accent} opacity="0.9" />}
      </svg>
      <div className="flex items-center gap-2 px-3 pb-3">
        <button onClick={() => setPlaying(!playing)} className="w-8 h-8 rounded-full flex items-center justify-center text-black" style={{ background: accent }} aria-label={playing ? "Pause" : "Play"}>
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button onClick={() => setSpeed(speed === 1 ? 0.5 : speed === 0.5 ? 0.25 : 1)} className="h-8 px-2.5 rounded-full border border-[#343A4A] text-xs inline-flex items-center gap-1"><Gauge className="w-3.5 h-3.5" />{speed}x</button>
        <input type="range" min="0" max="1" step="0.005" value={t} onChange={(e) => { setPlaying(false); setT(Number(e.target.value)); }} className="flex-1 accent-[#CCFF00]" aria-label="Scrub" />
      </div>
      {showCues && <p className="px-4 pb-3 -mt-1 text-sm font-medium" style={{ color: accent }}>{cue}</p>}
    </div>
  );
}

export function ExerciseCoachCard({ name, accent }) {
  const d = DEMOS[name];
  if (!d) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <ExerciseDemo name={name} accent={accent} />
      <div className="space-y-3">
        <div className="rounded-lg border border-[#1E222A] bg-[#0B0D11] p-3">
          <p className="mono-label text-acid mb-1">key cues</p>
          <ul className="text-sm space-y-1">{d.cues.map((c) => <li key={c}>✓ {c}</li>)}</ul>
        </div>
        <div className="rounded-lg border border-[#FF6B6B]/30 bg-[#0B0D11] p-3">
          <p className="mono-label text-[#FF6B6B] mb-1">common faults</p>
          <ul className="text-sm space-y-1 text-[#D7DCE5]">{d.faults.map((c) => <li key={c}>✗ {c}</li>)}</ul>
        </div>
        <p className="text-xs text-[#565C6F]">Start light and own the positions before adding load. If anything hurts, stop and ask a coach.</p>
      </div>
    </div>
  );
}
